// Panel state keyed by the pog atom, surviving tab switches. The component's local `previewOn` resets to false on every remount by design, so the persisted flag is the only witness that a preview is applied.
import type { Pog } from "@CommonTypes/merchflow/pog/pog";

export interface PanelPreviewState {
  originalPog: Pog;
  previewOn: boolean;
  regenerated: boolean;
}

const PANEL_STATE = new WeakMap<object, PanelPreviewState>();

export const initPanelPreviewState = (key: object, originalPog: Pog): PanelPreviewState => {
  const existing = PANEL_STATE.get(key);
  if (existing) return existing;
  const created: PanelPreviewState = { originalPog, previewOn: false, regenerated: false };
  PANEL_STATE.set(key, created);
  return created;
};

export const getPanelPreviewState = (key: object): PanelPreviewState | undefined =>
  PANEL_STATE.get(key);

export const resetPanelPreviewState = (key: object, originalPog: Pog): PanelPreviewState => {
  const created: PanelPreviewState = { originalPog, previewOn: false, regenerated: false };
  PANEL_STATE.set(key, created);
  return created;
};

export const setPanelPreviewOn = (key: object, previewOn: boolean): void => {
  const entry = PANEL_STATE.get(key);
  if (entry) entry.previewOn = previewOn;
};

export const setPanelRegenerated = (key: object, regenerated: boolean): void => {
  const entry = PANEL_STATE.get(key);
  if (entry) entry.regenerated = regenerated;
};

/** Restore is owed when either the live or the persisted flag says a preview is applied. */
export const consumePreviewOnClose = (key: object, localPreviewOn: boolean): boolean => {
  const entry = PANEL_STATE.get(key);
  const shouldRestore = localPreviewOn || entry?.previewOn === true;
  if (entry) entry.previewOn = false;
  return shouldRestore;
};
